import * as turf from "@turf/turf";
import { type ProductivityZonesInterface } from "../../pages/autozones/types";
import {
  getAccessibilityPaletteValue,
  getColorForNDVIValue,
  getDefaultColorPaletteValue
} from "../../pages/autozones/utils/autozoneColors";

// create addProductivityZonesLayer
export const addAutoZonesLayer = (
  map: mapboxgl.Map,
  productivityZonesData: ProductivityZonesInterface,
  autozoneOpacity: number,
  autozoneColorPalette?: string
): void => {
  const productivityZones =
    productivityZonesData.results[0].predictions[0].productivity_zones.features.map(
      (feature, index) => {
        /* this p50 variable can be modified for better visualizion of data (testing purposes) */
        const p50 = feature.properties.percentiles.p50;

        let color = "";
        if (autozoneColorPalette === "NDVI") {
          color = getColorForNDVIValue(p50);
        } else if (autozoneColorPalette === "DEFAULT") {
          color = getDefaultColorPaletteValue(
            index,
            productivityZonesData.results[0].predictions[0].productivity_zones
              .features.length
          );
        } else if (autozoneColorPalette === "ACCESSIBILITY") {
          color = getAccessibilityPaletteValue(
            index,
            productivityZonesData.results[0].predictions[0].productivity_zones
              .features.length
          );
        }

        return {
          ...feature,
          properties: {
            ...feature.properties,
            color,
          },
        };
      }
    );

  const productivityZonesGeoJson: any = {
    type: "FeatureCollection",
    features: productivityZones,
  };

  const opacity = autozoneOpacity / 100;

  if (map.getSource("productivity-zones")) {
    const productivityZonesSource = map.getSource(
      "productivity-zones"
    ) as mapboxgl.GeoJSONSource;
    productivityZonesSource.setData(productivityZonesGeoJson);

    if (map.getLayer("productivity-zones")) {
      map.setPaintProperty("productivity-zones", "fill-opacity", opacity);
    } else {
      map.addLayer(
        {
          id: "productivity-zones",
          type: "fill",
          source: "productivity-zones",
          paint: {
            "fill-color": ["get", "color"],
            "fill-opacity": opacity,
          },
        },
        "polygon-name"
      );
    }
  } else {
    map.addSource("productivity-zones", {
      type: "geojson",
      data: productivityZonesGeoJson,
    });
    map.addLayer(
      {
        id: "productivity-zones",
        type: "fill",
        source: "productivity-zones",
        paint: {
          "fill-color": ["get", "color"],
          "fill-opacity": opacity,
        },
      },
      "polygon-name" //  the 'before' parameter. new layer will be added before this layer
    );
  }
};

export const calculateFeatureArea = (
  feature: ProductivityZonesInterface["results"][0]["predictions"][0]["productivity_zones"]["features"][0]
): number => {
  // Filter the coordinates array to include only rings with 4 or more positions

  let geometry: any = null;
  geometry = turf.multiPolygon(feature.geometry.coordinates as any);

  return turf.area(geometry);
};
interface ZoneAreaResult {
  zonePercentage: number;
  zoneArea: number;
}

export const calculateZoneArea = (
  fieldPolygon: any,
  zoneData: ProductivityZonesInterface["results"][0]["predictions"][0]["productivity_zones"]["features"][0]
): ZoneAreaResult => {
  if (zoneData && fieldPolygon) {
    const zoneAreaSqMeters = calculateFeatureArea(zoneData);
    const zoneArea = zoneAreaSqMeters / 10000; // Convert square meters to hectares
    const areaField = fieldPolygon.calculated_area;

    const zonePercentage = (zoneArea / areaField) * 100;

    return { zonePercentage, zoneArea };
  }
  return { zonePercentage: 0, zoneArea: 0 };
};
